import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsIcon from '@mui/icons-material/Directions';
import { format } from 'date-fns';
import { projectsApi, tenantApi } from '../services/api';
import OrganisationContactSelector from './OrganisationContactSelector';

function ProjectDetailsTab({ project, organisation, contact, currentUser, onProjectUpdate, onProjectDelete }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editedProject, setEditedProject] = useState(project);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Organisation and contact state for editing
  const [selectedOrganisation, setSelectedOrganisation] = useState(organisation);
  const [selectedContact, setSelectedContact] = useState(contact);
  const [originalOrganisation, setOriginalOrganisation] = useState(organisation);
  const [originalContact, setOriginalContact] = useState(contact);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Departments state
  const [departments, setDepartments] = useState(['General']);

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await tenantApi.getBranding();
        const depts = response.data?.branding?.departments;
        if (Array.isArray(depts) && depts.length > 0) {
          setDepartments(depts);
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  // Update edited project when the project prop changes
  useEffect(() => {
    setEditedProject(project);
  }, [project]);

  // Update organisation and contact when props change
  useEffect(() => {
    setSelectedOrganisation(organisation);
    setOriginalOrganisation(organisation);
  }, [organisation]);

  useEffect(() => {
    setSelectedContact(contact);
    setOriginalContact(contact);
  }, [contact]);

  // Helper function to get address string for directions
  const getAddressString = () => {
    const parts = [
      editedProject.address_line1,
      editedProject.address_line2,
      editedProject.city,
      editedProject.postcode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Function to open directions in maps
  const openDirections = () => {
    const address = getAddressString();
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://maps.google.com/maps?q=${encodedAddress}`, '_blank');
    }
  };

  // Check if current user can edit/delete projects
  const canEditProject = () => {
    return currentUser?.role === 'Office';
  };
  const canViewProjectDuration = currentUser?.role === 'Office';
  const canViewProjectDueDate = currentUser?.role === 'Office';

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'dd MMM yyyy');
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original values
      setEditedProject(project);
      setSelectedOrganisation(originalOrganisation);
      setSelectedContact(originalContact);
    }
    setIsEditing(!isEditing);
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setEditedProject({
      ...editedProject,
      [field]: value,
    });
  };

  // Handle save
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Prepare data for API
      const updateData = {
        organisation_name: editedProject.organisation_name,
        address_line1: editedProject.address_line1,
        address_line2: editedProject.address_line2,
        city: editedProject.city,
        postcode: editedProject.postcode,
        what_three_words: editedProject.what_three_words,
        duration_in_days: editedProject.duration_in_days ? parseInt(editedProject.duration_in_days, 10) : null,
        quote_reference: editedProject.quote_reference,
        quote_type: editedProject.quote_type,
        customer_reference: editedProject.customer_reference,
        sales_order_reference: editedProject.sales_order_reference,
        on_site_contact_name: editedProject.on_site_contact_name || null,
        on_site_contact_phone: editedProject.on_site_contact_phone || null,
        working_sat: editedProject.working_sat || false,
        working_sun: editedProject.working_sun || false,
        department: editedProject.department || departments[0] || '',
      };

      // Include due date if it exists
      if (editedProject.due_date) {
        updateData.due_date = editedProject.due_date;
      }

      // Include start date if it exists
      if (editedProject.start_date) {
        updateData.start_date = editedProject.start_date;
      }

      // Always include organisation and contact IDs (can be null to clear them)
      updateData.organisation_id = selectedOrganisation?.organisationid || null;
      updateData.organisation_contact_id = selectedContact?.organisation_contact_id || null;
      
      // Update organisation_name for backward compatibility
      if (selectedOrganisation) {
        updateData.organisation_name = selectedOrganisation.name;
      }

      // Call API to update project
      await projectsApi.update(project.project_id, updateData);

      // Update parent component
      if (onProjectUpdate) {
        onProjectUpdate({ 
          ...project, 
          ...editedProject,
          organisation_id: selectedOrganisation?.organisationid,
          organisation_contact_id: selectedContact?.organisation_contact_id
        });
      }

      // Update the original state to the new values
      setOriginalOrganisation(selectedOrganisation);
      setOriginalContact(selectedContact);

      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'Project details updated successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating project:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update project details',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      setLoading(true);
      
      // Call API to delete project (soft delete)
      await projectsApi.delete(project.project_id);

      // Call parent component callback
      if (onProjectDelete) {
        onProjectDelete(project.project_id);
      }

      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Project deleted successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete project',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Render edit field helper
  const renderField = (label, field, value, multiline = false, type = 'text') => {
    if (isEditing && canEditProject() && field !== 'status') {
      if (type === 'date') {
        return (
          <TextField
            label={label}
            value={editedProject[field] ? editedProject[field].split('T')[0] : ''}
            onChange={(e) => handleFieldChange(field, e.target.value ? `${e.target.value}T00:00:00` : '')}
            size="small"
            fullWidth
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 1 }}
          />
        );
      }
      
      return (
        <TextField
          label={label}
          value={editedProject[field] || ''}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          size="small"
          fullWidth
          multiline={multiline}
          rows={multiline ? 2 : 1}
          type={type}
          sx={{ mb: 1 }}
        />
      );
    }
    return value || 'N/A';
  };

  // Mobile card component for displaying key-value pairs
  const InfoCard = ({ title, data, showProjectId = false }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
            {title}
          </Typography>
          {title === 'Project Information' && canEditProject() && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isEditing ? (
                <>
                  <IconButton size="small" onClick={handleEditToggle} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => setDeleteDialogOpen(true)} 
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton 
                    size="small" 
                    onClick={handleSave} 
                    color="primary"
                    disabled={loading}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={handleEditToggle} 
                    color="secondary"
                    disabled={loading}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        {/* Show OrganisationContactSelector when editing customer info */}
        {title === 'Customer Information' && isEditing && canEditProject() ? (
          <OrganisationContactSelector
            selectedOrganisation={selectedOrganisation}
            selectedContact={selectedContact}
            onOrganisationChange={setSelectedOrganisation}
            onContactChange={setSelectedContact}
          />
        ) : (
          <>
            {showProjectId && (
              <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                  Project No
                </Typography>
                <Chip 
                  label={`#${project.project_no || project.project_id}`} 
                  variant="outlined" 
                  size="small" 
                  sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {data.map((item, index) => (
                <Box key={index}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );

  // Prepare customer data - use organisation data if available
  const customerData = [
    { label: 'Organisation Name', value: selectedOrganisation?.name || editedProject.organisation_name || 'N/A' },
    { label: 'Email', value: selectedOrganisation?.email || 'N/A' },
    { label: 'Phone', value: selectedOrganisation?.phone || 'N/A' },
    { label: 'Organisation Address', value: selectedOrganisation?.address || 'N/A' },
    ...(selectedContact ? [
      { label: 'Contact Name', value: selectedContact.full_name || `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim() || 'N/A' },
      ...(selectedContact.email ? [{ label: 'Contact Email', value: selectedContact.email }] : []),
      ...(selectedContact.mobile ? [{ label: 'Contact Mobile', value: selectedContact.mobile }] : []),
    ] : []),
  ];

  // Prepare project address data - this is separate from customer info
  const projectAddressData = [
    { label: 'Address Line 1', value: renderField('Address Line 1', 'address_line1', editedProject.address_line1) },
    ...(editedProject.address_line2 || isEditing ? [{ label: 'Address Line 2', value: renderField('Address Line 2', 'address_line2', editedProject.address_line2) }] : []),
    { label: 'City', value: renderField('City', 'city', editedProject.city) },
    { label: 'Postcode', value: renderField('Postcode', 'postcode', editedProject.postcode) },
    ...(editedProject.what_three_words || isEditing ? [{ label: 'What3Words', value: renderField('What3Words', 'what_three_words', editedProject.what_three_words) }] : []),
  ];

  // Prepare project data
  const projectData = [
    { label: 'Status', value: renderField('Status', 'status', editedProject.status) },
    ...(canViewProjectDuration
      ? [{ label: 'Duration (days)', value: renderField('Duration (days)', 'duration_in_days', editedProject.duration_in_days, false, 'number') }]
      : []),
    {
      label: 'Department',
      value: isEditing && canEditProject() ? (
        <FormControl size="small" fullWidth>
          <InputLabel>Department</InputLabel>
          <Select
            value={editedProject.department || departments[0] || ''}
            onChange={(e) => handleFieldChange('department', e.target.value)}
            label="Department"
          >
            {departments.map((dept) => (
              <MenuItem key={dept} value={dept}>{dept}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (editedProject.department || 'N/A')
    },
    { 
      label: 'Working Days', 
      value: isEditing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={editedProject.working_sat || false}
                onChange={(e) => handleFieldChange('working_sat', e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Saturday</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={editedProject.working_sun || false}
                onChange={(e) => handleFieldChange('working_sun', e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Sunday</Typography>}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {editedProject.working_sat && <Chip label="Saturday" size="small" variant="outlined" />}
          {editedProject.working_sun && <Chip label="Sunday" size="small" variant="outlined" />}
          {!editedProject.working_sat && !editedProject.working_sun && 'Mon-Fri only'}
        </Box>
      )
    },
    { label: 'Created Date', value: formatDate(editedProject.created_date) },
    { label: 'Created By', value: editedProject.created_by_name || 'N/A' },
    ...(editedProject.status === 'Quote' 
      ? [
          { label: 'Quote Reference', value: renderField('Quote Reference', 'quote_reference', editedProject.quote_reference) },
          { label: 'Quote Type', value: renderField('Quote Type', 'quote_type', editedProject.quote_type) }
        ]
      : [
          { label: 'Customer Reference', value: renderField('Customer Reference', 'customer_reference', editedProject.customer_reference) },
          { label: 'Start Date', value: isEditing ? renderField('Start Date', 'start_date', editedProject.start_date, false, 'date') : (editedProject.start_date ? formatDate(editedProject.start_date) : 'N/A') },
          ...(canViewProjectDueDate
            ? [{ label: 'Due Date', value: editedProject.due_date ? (isEditing ? renderField('Due Date', 'due_date', editedProject.due_date, false, 'date') : formatDate(editedProject.due_date)) : 'N/A' }]
            : []),
          { label: 'Sales Order Reference', value: renderField('Sales Order Reference', 'sales_order_reference', editedProject.sales_order_reference) },
          { label: 'On-site Contact Name', value: renderField('On-site Contact Name', 'on_site_contact_name', editedProject.on_site_contact_name) },
          { label: 'On-site Contact Phone', value: renderField('On-site Contact Phone', 'on_site_contact_phone', editedProject.on_site_contact_phone) },
        ]
    ),
  ];

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Mobile view - Cards */}
      {isMobile ? (
        <Box>
          <InfoCard title="Project Information" data={projectData} showProjectId={true} />
          <InfoCard title="Customer Information" data={customerData} />
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
                  Project Address
                </Typography>
                <IconButton size="small" onClick={openDirections} color="primary">
                  <DirectionsIcon fontSize="small" />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {projectAddressData.map((item, index) => (
                  <Box key={index}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : (
        /* Desktop view - Grid with Tables */
        <Grid container spacing={3}>
          {/* Project Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              {/* Header with Project No and Edit/Delete buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
                    Project Information
                  </Typography>
                  <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                      Project No
                    </Typography>
                    <Chip 
                      label={`#${project.project_no || project.project_id}`} 
                      variant="outlined" 
                      size="small" 
                      sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                    />
                  </Box>
                </Box>
                {canEditProject() && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isEditing ? (
                      <>
                        <IconButton size="small" onClick={handleEditToggle} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => setDeleteDialogOpen(true)} 
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton 
                          size="small" 
                          onClick={handleSave} 
                          color="primary"
                          disabled={loading}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={handleEditToggle} 
                          color="secondary"
                          disabled={loading}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold', width: '20%' }}>
                        Status
                      </TableCell>
                      <TableCell>{renderField('Status', 'status', editedProject.status)}</TableCell>
                    </TableRow>
                    {canViewProjectDuration && (
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                          Duration (days)
                        </TableCell>
                        <TableCell>{renderField('Duration (days)', 'duration_in_days', editedProject.duration_in_days, false, 'number')}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Department
                      </TableCell>
                      <TableCell>
                        {isEditing && canEditProject() ? (
                          <FormControl size="small" fullWidth>
                            <InputLabel>Department</InputLabel>
                            <Select
                              value={editedProject.department || departments[0] || ''}
                              onChange={(e) => handleFieldChange('department', e.target.value)}
                              label="Department"
                            >
                              {departments.map((dept) => (
                                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (editedProject.department || 'N/A')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Working Days
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={editedProject.working_sat || false}
                                  onChange={(e) => handleFieldChange('working_sat', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Saturday</Typography>}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={editedProject.working_sun || false}
                                  onChange={(e) => handleFieldChange('working_sun', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Sunday</Typography>}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {editedProject.working_sat && <Chip label="Saturday" size="small" variant="outlined" />}
                            {editedProject.working_sun && <Chip label="Sunday" size="small" variant="outlined" />}
                            {!editedProject.working_sat && !editedProject.working_sun && 'Mon-Fri only'}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Created Date
                      </TableCell>
                      <TableCell>{formatDate(editedProject.created_date)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Created By
                      </TableCell>
                      <TableCell>{editedProject.created_by_name || 'N/A'}</TableCell>
                    </TableRow>

                    {editedProject.status === 'Quote' && (
                      <>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            Quote Reference
                          </TableCell>
                          <TableCell>{renderField('Quote Reference', 'quote_reference', editedProject.quote_reference)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            Quote Type
                          </TableCell>
                          <TableCell>{renderField('Quote Type', 'quote_type', editedProject.quote_type)}</TableCell>
                        </TableRow>
                      </>
                    )}

                    {editedProject.status !== 'Quote' && (
                      <>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            Customer Reference
                          </TableCell>
                          <TableCell>{renderField('Customer Reference', 'customer_reference', editedProject.customer_reference)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            Start Date
                          </TableCell>
                          <TableCell>
                            {isEditing 
                              ? renderField('Start Date', 'start_date', editedProject.start_date, false, 'date') 
                              : (editedProject.start_date ? formatDate(editedProject.start_date) : 'N/A')
                            }
                          </TableCell>
                        </TableRow>
                        {canViewProjectDueDate && (
                          <TableRow>
                            <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                              Due Date
                            </TableCell>
                            <TableCell>
                              {editedProject.due_date 
                                ? (isEditing 
                                    ? renderField('Due Date', 'due_date', editedProject.due_date, false, 'date') 
                                    : formatDate(editedProject.due_date)
                                  ) 
                                : 'N/A'
                              }
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            Quote Reference
                          </TableCell>
                          <TableCell>{renderField('Quote Reference', 'quote_reference', editedProject.quote_reference)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            On-site Contact Name
                          </TableCell>
                          <TableCell>{renderField('On-site Contact Name', 'on_site_contact_name', editedProject.on_site_contact_name)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            On-site Contact Phone
                          </TableCell>
                          <TableCell>{renderField('On-site Contact Phone', 'on_site_contact_phone', editedProject.on_site_contact_phone)}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Customer Information - Read-only display, editable via Project Information edit mode */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600, mb: 2 }}>
                Customer Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {isEditing && canEditProject() ? (
                <OrganisationContactSelector
                  selectedOrganisation={selectedOrganisation}
                  selectedContact={selectedContact}
                  onOrganisationChange={setSelectedOrganisation}
                  onContactChange={setSelectedContact}
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold', width: '40%' }}>
                          Organisation Name
                        </TableCell>
                        <TableCell>{selectedOrganisation?.name || editedProject.organisation_name || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                          Email
                        </TableCell>
                        <TableCell>{selectedOrganisation?.email || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                          Phone
                        </TableCell>
                        <TableCell>{selectedOrganisation?.phone || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                          Organisation Address
                        </TableCell>
                        <TableCell>{selectedOrganisation?.address || 'N/A'}</TableCell>
                      </TableRow>
                      {selectedContact && (
                        <>
                          <TableRow>
                            <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                              Contact Name
                            </TableCell>
                            <TableCell>{selectedContact.full_name || `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim() || 'N/A'}</TableCell>
                          </TableRow>
                          {selectedContact.email && (
                            <TableRow>
                              <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                                Contact Email
                              </TableCell>
                              <TableCell>{selectedContact.email}</TableCell>
                            </TableRow>
                          )}
                          {selectedContact.mobile && (
                            <TableRow>
                              <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                                Contact Mobile
                              </TableCell>
                              <TableCell>{selectedContact.mobile}</TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Project Address - Editable */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
                  Project Address
                </Typography>
                <IconButton size="small" onClick={openDirections} color="primary">
                  <DirectionsIcon fontSize="small" />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold', width: '40%' }}>
                        Address Line 1
                      </TableCell>
                      <TableCell>{renderField('Address Line 1', 'address_line1', editedProject.address_line1)}</TableCell>
                    </TableRow>
                    {(editedProject.address_line2 || isEditing) && (
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                          Address Line 2
                        </TableCell>
                        <TableCell>{renderField('Address Line 2', 'address_line2', editedProject.address_line2)}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        City
                      </TableCell>
                      <TableCell>{renderField('City', 'city', editedProject.city)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Postcode
                      </TableCell>
                      <TableCell>{renderField('Postcode', 'postcode', editedProject.postcode)}</TableCell>
                    </TableRow>
                    {(editedProject.what_three_words || isEditing) && (
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                          What3Words
                        </TableCell>
                        <TableCell>{renderField('What3Words', 'what_three_words', editedProject.what_three_words)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Project
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this project?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            Delete Project
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectDetailsTab;
